// owner/screens/bookings/BookingsList.js — the owner bookings list, built ONCE (Adil, 4 Nov 2026) and used by
// /bookings (all units or one, via the unit picker) and by a unit's bookings tab (its unit fixed).
//   useOwnerBookings — the filter state and the paged read: search (debounced) and status run on the server, so
//                      `total` describes the whole filtered set; latest check-in first, 20 at a time;
//   BookingFilters   — the search box and the status chips الكل / قادم / مقيم الآن / مكتمل / ملغي;
//   BookingRows      — the rows (BookingRow "booking"), «عرض المزيد», and loading / error / empty states.
// No date maths here: dates, nights and every count come from the server (GET /api/v1/bookings, owner allow-list).

import React, { useEffect, useState } from 'react';
import useLanguage from '../../../hooks/useLanguage';
import { fill } from '../../../utils/localeText';
import { useBookingsPaged } from '../../../hooks/useBookingsPaged';
import Icon from '../../ui/Icon';
import BookingRow from '../../ui/BookingRow';
import { InlineError } from '../../ui/States';
import useOwnerFormat from '../../ui/useOwnerFormat';

const STATUSES = ['', 'upcoming', 'active', 'completed', 'cancelled'];  // '' = all (Booking.status enum)
const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 350;

/** Filter state + the paged list. `propertyId` '' or undefined = every unit the owner owns. */
export const useOwnerBookings = (propertyId) => {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [term, setTerm] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setTerm(search.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  const list = useBookingsPaged({
    propertyId: propertyId || undefined,
    status: status || undefined,
    search: term || undefined
  }, PAGE_SIZE);

  return { search, setSearch, status, setStatus, filtered: Boolean(status || term), list };
};

export const BookingFilters = ({ state, searchHint }) => {
  const { t } = useLanguage();
  return (
    <div className="o-filters">
      <label className="o-search">
        <span className="o-visually-hidden">{searchHint}</span>
        <Icon name="search" size="sm" />
        <input type="search" value={state.search} onChange={(e) => state.setSearch(e.target.value)} placeholder={searchHint} />
      </label>
      <div className="o-seg" role="group" aria-label={t('ownerApp.bookings.statusFilter')}>
        {STATUSES.map((key) => (
          <button key={key || 'all'} type="button" aria-pressed={state.status === key} onClick={() => state.setStatus(key)}>
            {key ? t(`ownerApp.bookingStatus.${key}`) : t('ownerApp.bookings.all')}
          </button>
        ))}
      </div>
    </div>
  );
};

/** `withUnit`: show each booking's unit (the all-units list); `photoById`: unit photos from the cached units list. */
export const BookingRows = ({ state, withUnit = false, photoById = {} }) => {
  const { t } = useLanguage();
  const f = useOwnerFormat();
  const { list, filtered } = state;

  if (list.isLoading) return <div className="o-sk" style={{ height: 260, borderRadius: 20 }} aria-busy="true" />;
  if (list.isError && list.bookings.length === 0) {
    return <div className="o-sheet"><InlineError text={t('ownerApp.bookings.loadError')} onRetry={list.refetch} /></div>;
  }
  if (list.bookings.length === 0) {
    return <div className="o-sheet"><p className="o-empty-row">{t(filtered ? 'ownerApp.bookings.noMatch' : 'ownerApp.bookings.empty')}</p></div>;
  }

  return (
    <>
      {/* a changed filter keeps the previous rows on screen, dimmed, until the new ones arrive */}
      <ul className={`o-sheet o-rows o-bk-list${list.isPlaceholderData ? ' o-stale' : ''}`} aria-busy={list.isPlaceholderData || undefined}>
        {list.bookings.map((b, i) => (
          <BookingRow key={b._id} booking={b} variant="booking" withUnit={withUnit}
            photo={photoById[String(b.property?._id)]} index={i} />
        ))}
      </ul>
      <div className="o-list-foot">
        <span>{fill(t('lists.showingOf'), { shown: f.count(list.bookings.length), total: f.count(list.total) })}</span>
        {list.hasNextPage && (
          <button type="button" className="o-btn o-btn-outline" onClick={() => list.fetchNextPage()} disabled={list.isFetchingNextPage}>
            {list.isFetchingNextPage ? t('lists.loadingMore') : t('lists.loadMore')}
          </button>
        )}
      </div>
      {list.isFetchNextPageError && <InlineError text={t('lists.loadMoreFailed')} onRetry={() => list.fetchNextPage()} />}
    </>
  );
};
